import {
  buildSyntheticDataDemoAnswerBlocks,
  SYNTHETIC_DATA_DEMO_THOUGHT_DETAILS,
  SYNTHETIC_DATA_DEMO_WORKFLOW_STEPS,
} from '../demo/syntheticDataDemoContent.js'
import { buildSyntheticDataDemoArtifactPreview } from '../demo/syntheticDataDemoArtifact.js'
import { registerArtifactPayload } from '@/features/documentPreview/artifactPayloadStore'
import { randomBetween } from '../streaming/computeStreamTypingDuration.js'
import { scaleChatStreamDelay } from '../streaming/chatStreamSpeed.js'
import { updatePendingSyntheticDataDemoMessage } from './patchSyntheticDataDemoMessage.js'
import { runWorkflowStepsInStream } from './runWorkflowStepsInStream.js'
import {
  runSyntheticDataDemoTypewriter,
  SYNTHETIC_DEMO_TYPEWRITER_OPTIONS,
  syntheticDemoTypewriterDurationMs,
} from './syntheticDataDemoTypewriter.js'

export function startSyntheticDataDemoStreamLoop(ctx) {
  const { tid, pendingId, signal, session, setMessagesByThreadId } = ctx
  const state = { thoughtDetails: /** @type {string[]} */ ([]), blocks: /** @type {object[]} */ ([]) }

  const patch = (fields) => updatePendingSyntheticDataDemoMessage(setMessagesByThreadId, tid, pendingId, fields)
  const schedule = (ms, fn) => {
    session.addTimeout(() => {
      if (signal.aborted) {
        session.dispose()
        return
      }
      fn()
    }, ms)
  }
  const runAfter = (ms, fn) => schedule(scaleChatStreamDelay(ms), fn)
  const runTypewriterText = (text, onUpdate, onDone) => {
    runSyntheticDataDemoTypewriter({ session, signal, text, onUpdate, onDone })
  }

  const runThoughtParagraphs = (onDone) => {
    let paraIdx = 0
    const runNext = () => {
      if (paraIdx >= SYNTHETIC_DATA_DEMO_THOUGHT_DETAILS.length) {
        patch({ thoughtSummary: 'Thought for 6s', thoughtDetails: [...state.thoughtDetails] })
        runAfter(120, onDone)
        return
      }
      const fullText = SYNTHETIC_DATA_DEMO_THOUGHT_DETAILS[paraIdx]
      state.thoughtDetails.push('')
      const idx = state.thoughtDetails.length - 1
      patch({ thoughtDetails: [...state.thoughtDetails], thoughtSummary: `Thought for ${2 * (paraIdx + 1)}s` })
      runTypewriterText(
        fullText,
        (partial) => {
          state.thoughtDetails[idx] = partial
          patch({ thoughtDetails: [...state.thoughtDetails] })
        },
        () => {
          paraIdx++
          runAfter(95, runNext)
        },
      )
    }
    runNext()
  }

  const runWorkflowSteps = (onDone) => {
    const logIndex = state.blocks.length
    state.blocks.push({ type: 'aiResponseLog', status: 'ok', showLogStatus: false, steps: [] })
    const patchLog = (steps) => {
      state.blocks[logIndex] = { type: 'aiResponseLog', status: 'ok', showLogStatus: false, steps }
      patch({ blocks: [...state.blocks] })
    }
    runWorkflowStepsInStream({
      templates: SYNTHETIC_DATA_DEMO_WORKFLOW_STEPS,
      session,
      signal,
      runAfter: schedule,
      onStepsChange: patchLog,
      onDone,
      speedFactor: 1,
      typewriterOptions: SYNTHETIC_DEMO_TYPEWRITER_OPTIONS,
      getTypewriterDurationMs: syntheticDemoTypewriterDurationMs,
    })
  }

  const runMarkdownType = (content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'markdown', content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterText(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'markdown', content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runHeadingType = (level, content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'heading', level, content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterText(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'heading', level, content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runListType = (block, onDone) => {
    const blockIndex = state.blocks.length
    const items = block.items ?? []
    state.blocks.push({ type: 'list', items: [], ordered: block.ordered })
    patch({ blocks: [...state.blocks] })

    let itemIdx = 0
    const runNextItem = () => {
      if (itemIdx >= items.length) {
        onDone()
        return
      }
      const itemText = items[itemIdx]
      state.blocks[blockIndex].items.push('')
      const currentItemIndex = itemIdx
      patch({ blocks: [...state.blocks] })
      runTypewriterText(
        itemText,
        (partial) => {
          state.blocks[blockIndex].items[currentItemIndex] = partial
          patch({ blocks: [...state.blocks] })
        },
        () => {
          itemIdx++
          runAfter(75, runNextItem)
        },
      )
    }
    runNextItem()
  }

  const runAnswerBlocks = (onDone) => {
    const templates = buildSyntheticDataDemoAnswerBlocks()
    let blockIdx = 0

    const runNext = () => {
      if (blockIdx >= templates.length) {
        onDone()
        return
      }
      const block = templates[blockIdx]
      blockIdx++

      if (block.type === 'divider') {
        state.blocks.push({ type: 'divider' })
        patch({ blocks: [...state.blocks] })
        runAfter(90, runNext)
        return
      }
      if (block.type === 'markdown') {
        runMarkdownType(block.content, () => runAfter(75, runNext))
        return
      }
      if (block.type === 'heading') {
        runHeadingType(block.level ?? 3, block.content, () => runAfter(75, runNext))
        return
      }
      if (block.type === 'list') {
        runListType(block, () => runAfter(75, runNext))
        return
      }
      runNext()
    }

    runNext()
  }

  patch({
    demoStreamActive: true,
    text: '',
    thoughtSummary: 'Thinking…',
    thoughtDetails: [],
    blocks: [],
    agentBrand: 'SIA',
    agentName: 'Master Root Agent',
  })

  registerArtifactPayload(buildSyntheticDataDemoArtifactPreview())

  runAfter(randomBetween(470, 670), () => {
    runThoughtParagraphs(() => {
      runWorkflowSteps(() => {
        runAnswerBlocks(() => {
          patch({ demoStreamActive: false, text: '', thoughtSummary: 'Thought for 6s', blocks: [...state.blocks] })
          session.dispose()
        })
      })
    })
  })
}
